import { useQuery } from '@tanstack/react-query'
import {
  ShipmentNotification,
  ShipmentNotificationParams,
  ShipmentNotificationStatus,
} from '@/interfaces/shipmentNotification'
import APIServices from '@/api/APIServices'
import { useShipmentNotificationFilters } from '../filters/hooks/useShipmentNotificationFilters'
import { useUpdateShipmentNotification } from './useUpdateShipmentNotification'
import { useDeleteShipmentNotification } from './useDeleteShipmentNotification'
import { getProviders } from '@/api/providersAPI'
import { getManagers } from '@/api/managersAPI'
import { getUsers } from '@/api/usersAPI'

// Función para obtener notificaciones desde la API
const fetchShipmentNotifications = async (
  pageSize = 50,
  filters?: {
    search?: string
    shouldArrivedAtFrom?: string
    shouldArrivedAtTo?: string
    status?: string[]
    providerCompanyId?: string[]
    managerCompanyId?: string[]
    createdById?: string[]
  }
): Promise<ShipmentNotification[]> => {
  // Construir parámetros de consulta para el backend
  const queryParams: {
    per_page: number
    search?: string
    shouldArrivedAtFrom?: string
    shouldArrivedAtTo?: string
    status?: string
    providerCompanyId?: string
    managerCompanyId?: string
    createdById?: string
  } = {
    per_page: pageSize,
  }

  // Agregar filtros si están disponibles
  if (filters) {
    if (filters.search) {
      queryParams.search = filters.search
    }

    if (filters.shouldArrivedAtFrom) {
      queryParams.shouldArrivedAtFrom = filters.shouldArrivedAtFrom
    }

    if (filters.shouldArrivedAtTo) {
      queryParams.shouldArrivedAtTo = filters.shouldArrivedAtTo
    }

    // Enviar filtro de estado al backend
    if (filters.status && filters.status.length > 0) {
      if (filters.status.includes('all')) {
        // Si se selecciona "todos", enviar todos los estados disponibles
        queryParams.status = 'CREATED,PENDING_TO_ARRIVE,ARRIVED,CANCELLED'
      } else {
        // Si se seleccionan estados específicos, enviar solo esos
        queryParams.status = filters.status.join(',')
      }
    }

    if (
      filters.providerCompanyId &&
      filters.providerCompanyId.length > 0 &&
      !filters.providerCompanyId.includes('all')
    ) {
      queryParams.providerCompanyId = filters.providerCompanyId.join(',')
    }

    if (
      filters.managerCompanyId &&
      filters.managerCompanyId.length > 0 &&
      !filters.managerCompanyId.includes('all')
    ) {
      queryParams.managerCompanyId = filters.managerCompanyId.join(',')
    }

    if (
      filters.createdById &&
      filters.createdById.length > 0 &&
      !filters.createdById.includes('all')
    ) {
      queryParams.createdById = filters.createdById.join(',')
    }
  }

  // First, fetch the notifications with filters
  const notificationsResponse = await APIServices.get(
    '/cargo/shippingnotification',
    {
      params: queryParams,
    }
  )

  // Get notifications array from response
  let notifications: unknown[] = []
  if (Array.isArray(notificationsResponse.data)) {
    notifications = notificationsResponse.data
  } else if (Array.isArray(notificationsResponse)) {
    notifications = notificationsResponse
  } else if (
    notificationsResponse.data &&
    Array.isArray(notificationsResponse.data.items)
  ) {
    notifications = notificationsResponse.data.items
  } else if (
    notificationsResponse.data &&
    Array.isArray(notificationsResponse.data.data)
  ) {
    notifications = notificationsResponse.data.data
  }

  // If no notifications, return empty array
  if (notifications.length === 0) {
    return []
  }

  // Fetch reference data in parallel (non-blocking) using the pageSize from table context
  let providersData: unknown[] = []
  let managersData: unknown[] = []
  let usersData: unknown[] = []

  try {
    const [providers, managers, users] = await Promise.allSettled([
      getProviders({ page: 1, per_page: pageSize, search: '' }),
      getManagers({ page: 1, per_page: pageSize, search: '' }),
      getUsers({ page: 1, per_page: pageSize, search: '' }),
    ])

    if (providers.status === 'fulfilled') {
      // The API now returns the items directly
      const providersResponse = providers.value
      providersData = Array.isArray(providersResponse) ? providersResponse : []
    }

    if (managers.status === 'fulfilled') {
      // The API now returns the items directly
      const managersResponse = managers.value
      managersData = Array.isArray(managersResponse) ? managersResponse : []
    }

    if (users.status === 'fulfilled') {
      // getUsers devuelve directamente un array de usuarios
      usersData = users.value || []
    }
  } catch {
    // Silently handle reference data errors
  }

  // Create lookup maps for names
  const providerMap = new Map(
    providersData.map((p) => {
      const provider = p as { id: number; name: string }
      return [provider.id, provider.name]
    })
  )
  const managerMap = new Map(
    managersData.map((m) => {
      const manager = m as { id: number; name: string }
      return [manager.id, manager.name]
    })
  )
  const userMap = new Map(
    usersData.map((u) => {
      const user = u as {
        id: number
        displayName?: string
        username?: string
      }
      return [
        user.id,
        user.displayName || user.username || `Usuario ${user.id}`,
      ]
    })
  )

  // Enrich notifications with names
  const enrichedNotifications: ShipmentNotification[] = notifications.map(
    (notificationUnknown) => {
      const notification = notificationUnknown as ShipmentNotification
      // Extract IDs from the nested objects
      const providerCompanyId = notification.providerCompany?.id
      const managerCompanyId = notification.managerCompany?.id
      const createdById = notification.createdBy?.id

      // Get names from the maps or use the names directly from the notification
      const providerName =
        notification.providerCompany?.name ||
        (providerCompanyId ? providerMap.get(providerCompanyId) : undefined)
      const managerName =
        notification.managerCompany?.name ||
        (managerCompanyId ? managerMap.get(managerCompanyId) : undefined)
      const userName =
        notification.createdBy?.displayName ||
        (createdById ? userMap.get(createdById) : undefined)

      return {
        ...notification,
        // Add the ID fields for compatibility
        providerCompanyId,
        managerCompanyId,
        createdById,
        // Add the name fields
        providerCompanyName:
          providerName || `Proveedor ${providerCompanyId || 'N/A'}`,
        managerCompanyName:
          managerName || `Manager ${managerCompanyId || 'N/A'}`,
        createdByName: userName || `Usuario ${createdById || 'N/A'}`,
        // Fix: always provide shouldArrivedAt for filtering
        shouldArrivedAt: notification.shouldArrivedAt || '',
      }
    }
  )

  return enrichedNotifications
}

export function useShipmentNotifications(pageSize = 50) {
  const { filters } = useShipmentNotificationFilters()

  // Usar React Query para manejar la data de la API
  const {
    data: apiData = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['shipment-notifications', 'with-names', filters, pageSize], // Incluir pageSize en la queryKey
    queryFn: () => fetchShipmentNotifications(pageSize, filters),
    staleTime: 60 * 60 * 1000, // 1 hora
    gcTime: 2 * 60 * 60 * 1000, // 2 horas
  })

  // Hooks para operaciones CRUD
  const updateMutation = useUpdateShipmentNotification()
  const deleteMutation = useDeleteShipmentNotification()

  // Ensure apiData is always an array before filtering
  const validApiData = Array.isArray(apiData) ? apiData : []

  // Filter data based on current filters (solo filtros que no se pueden hacer en el backend)
  const filteredData = validApiData.filter(
    (notification: ShipmentNotification) => {
      // Search filter (si no se envió al backend)
      if (
        filters.search &&
        !notification.name?.toLowerCase().includes(filters.search.toLowerCase())
      ) {
        return false
      }

      // Date filters (solo la versión robusta y funcional)
      if (notification.shouldArrivedAt) {
        const notifDateStr = notification.shouldArrivedAt.split('T')[0]
        // Si solo hay un extremo, filtra por igualdad
        if (
          (filters.shouldArrivedAtFrom && !filters.shouldArrivedAtTo) ||
          (!filters.shouldArrivedAtFrom && filters.shouldArrivedAtTo)
        ) {
          const targetDate =
            filters.shouldArrivedAtFrom || filters.shouldArrivedAtTo
          if (notifDateStr !== targetDate) return false
        } else if (
          filters.shouldArrivedAtFrom &&
          filters.shouldArrivedAtTo &&
          filters.shouldArrivedAtFrom === filters.shouldArrivedAtTo
        ) {
          // Ambos extremos iguales (caso raro, pero por si acaso)
          if (notifDateStr !== filters.shouldArrivedAtFrom) return false
        } else {
          if (
            filters.shouldArrivedAtFrom &&
            notifDateStr < filters.shouldArrivedAtFrom
          )
            return false
          if (
            filters.shouldArrivedAtTo &&
            notifDateStr > filters.shouldArrivedAtTo
          )
            return false
        }
      }

      return true
    }
  )

  // Ordenar por fecha de arribo descendente (más reciente primero)
  const sortedData = filteredData.slice().sort((a, b) => {
    if (!a.shouldArrivedAt && !b.shouldArrivedAt) return 0
    if (!a.shouldArrivedAt) return 1
    if (!b.shouldArrivedAt) return -1
    // Orden descendente
    return b.shouldArrivedAt.localeCompare(a.shouldArrivedAt)
  })

  const addNotification = () => {
    // This will be handled by the POST hook and then refetch will be called
  }

  const updateNotification = (
    id: number,
    updates: Partial<ShipmentNotification>
  ) => {
    // Convertir updates a formato compatible con la API
    const apiData: ShipmentNotificationParams = {
      name: updates.name || '',
      shouldArrivedAt: updates.shouldArrivedAt || '',
      providerCompanyId: updates.providerCompanyId || 0,
      managerCompanyId: updates.managerCompanyId || 0,
      observations: updates.observations || '',
    }

    if (updates.status) {
      apiData.status = updates.status as ShipmentNotificationStatus
    }

    updateMutation.mutate({
      id,
      data: apiData,
    })
  }

  const deleteNotification = (id: number) => {
    deleteMutation.mutate(id)
  }

  return {
    data: sortedData,
    isLoading,
    error,
    refetch,
    addNotification,
    updateNotification,
    deleteNotification,
    updateMutation,
    deleteMutation,
  }
}
